/**
 * WebSocket Client for Real-Time Collaboration
 * 
 * Manages WebSocket connection to backend for real-time features:
 * - Collaborative cursors
 * - Comment synchronization
 * - Presence indicators
 * - Activity feed
 * 
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Connection state management
 * - Event-based message handling
 * - Heartbeat/ping-pong for connection health
 * 
 * Requirements: 15.1
 */

import { getApiBaseUrl } from '../config';

export type WebSocketConnectionState = 
  | 'disconnected' 
  | 'connecting' 
  | 'connected' 
  | 'reconnecting' 
  | 'error';

export type WebSocketEventType = 
  | 'open' 
  | 'close' 
  | 'error' 
  | 'message' 
  | 'reconnecting' 
  | 'reconnected';

export type WebSocketEventHandler = (event?: any) => void;

interface WebSocketClientConfig {
  /**
   * Project ID for collaboration session
   */
  projectId: string;
  
  /**
   * User ID for authentication
   */
  userId: string;
  
  /**
   * Authentication token
   */
  token?: string;
  
  /**
   * Enable automatic reconnection
   * @default true
   */
  autoReconnect?: boolean;
  
  /**
   * Initial reconnection delay in milliseconds
   * @default 1000
   */
  reconnectDelay?: number;
  
  /**
   * Maximum reconnection delay in milliseconds
   * @default 30000
   */
  maxReconnectDelay?: number;
  
  /**
   * Maximum number of reconnection attempts
   * @default Infinity
   */
  maxReconnectAttempts?: number;
  
  /**
   * Heartbeat interval in milliseconds
   * @default 30000
   */
  heartbeatInterval?: number;
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private config: Required<WebSocketClientConfig>;
  private connectionState: WebSocketConnectionState = 'disconnected';
  private eventHandlers: Map<WebSocketEventType, Set<WebSocketEventHandler>> = new Map();
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private lastHeartbeat: number = 0;
  private messageQueue: any[] = [];
  private batchQueue: any[] = [];
  private batchTimer: number | null = null;
  private batchInterval: number = 50; // Batch messages every 50ms (Requirement 15.3)
  private bandwidthLimited: boolean = false;
  private messagesSent: number = 0;
  private bandwidthCheckTimer: number | null = null;
  
  constructor(config: WebSocketClientConfig) {
    this.config = {
      autoReconnect: true,
      reconnectDelay: 1000,
      maxReconnectDelay: 30000,
      maxReconnectAttempts: Infinity,
      heartbeatInterval: 30000,
      ...config,
    };
    
    // Initialize event handler sets
    const eventTypes: WebSocketEventType[] = ['open', 'close', 'error', 'message', 'reconnecting', 'reconnected'];
    eventTypes.forEach(type => {
      this.eventHandlers.set(type, new Set());
    });
  }
  
  /**
   * Get WebSocket URL from API base URL
   */
  private getWebSocketUrl(): string {
    const apiBase = getApiBaseUrl();
    // Convert http(s) to ws(s)
    const wsBase = apiBase.replace(/^http/, 'ws');
    return `${wsBase}/ws/collaboration?projectId=${this.config.projectId}&userId=${this.config.userId}`;
  }
  
  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      console.warn('WebSocket already connected or connecting');
      return;
    }
    
    this.setConnectionState('connecting');
    
    try {
      const url = this.getWebSocketUrl();
      this.ws = new WebSocket(url);
      
      // Set up event handlers
      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.setConnectionState('error');
      this.emit('error', error);
      this.scheduleReconnect();
    }
  }
  
  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.clearReconnectTimer();
    this.clearHeartbeatTimer();
    this.clearBatchTimer();
    
    if (this.ws) {
      // Remove event handlers to prevent reconnection
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close(1000, 'Client disconnect');
      }
      
      this.ws = null;
    }
    
    this.setConnectionState('disconnected');
    this.reconnectAttempts = 0;
    this.messageQueue = [];
    this.batchQueue = [];
  }
  
  /**
   * Clear batch timer
   */
  private clearBatchTimer(): void {
    if (this.batchTimer !== null) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
  }
  
  /**
   * Start bandwidth monitoring (Requirement 15.2)
   * Monitors message send rate and adjusts throttling if needed
   */
  private startBandwidthMonitoring(): void {
    this.clearBandwidthMonitoring();
    this.messagesSent = 0;
    this.bandwidthLimited = false;
    
    // Check bandwidth every 5 seconds
    this.bandwidthCheckTimer = window.setInterval(() => {
      // If sending more than 100 messages per 5 seconds (20/sec), consider bandwidth limited
      if (this.messagesSent > 100) {
        if (!this.bandwidthLimited) {
          console.log('Bandwidth limited detected, reducing cursor update frequency to 5 Hz');
          this.bandwidthLimited = true;
        }
      } else {
        if (this.bandwidthLimited) {
          console.log('Bandwidth restored, increasing cursor update frequency to 10 Hz');
          this.bandwidthLimited = false;
        }
      }
      
      // Reset counter
      this.messagesSent = 0;
    }, 5000);
  }
  
  /**
   * Clear bandwidth monitoring
   */
  private clearBandwidthMonitoring(): void {
    if (this.bandwidthCheckTimer !== null) {
      clearInterval(this.bandwidthCheckTimer);
      this.bandwidthCheckTimer = null;
    }
    this.messagesSent = 0;
    this.bandwidthLimited = false;
  }
  
  /**
   * Send message to server
   * Supports batching for cursor updates (Requirement 15.3)
   */
  send(message: any, options?: { batch?: boolean }): void {
    if (this.connectionState === 'connected' && this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Check if message should be batched (cursor updates)
      if (options?.batch && message.type === 'cursor_update') {
        this.addToBatch(message);
        return;
      }
      
      try {
        const payload = typeof message === 'string' ? message : JSON.stringify(message);
        this.ws.send(payload);
        this.messagesSent++; // Track for bandwidth monitoring
      } catch (error) {
        console.error('Failed to send message:', error);
        // Queue message for retry
        this.messageQueue.push(message);
      }
    } else {
      // Queue message for when connection is restored
      this.messageQueue.push(message);
      console.warn('WebSocket not connected, message queued');
    }
  }
  
  /**
   * Add message to batch queue (Requirement 15.3)
   * Batches cursor updates to reduce network overhead
   */
  private addToBatch(message: any): void {
    // Add to batch queue
    this.batchQueue.push(message);
    
    // Start batch timer if not already running
    if (this.batchTimer === null) {
      this.batchTimer = window.setTimeout(() => {
        this.flushBatch();
      }, this.batchInterval);
    }
  }
  
  /**
   * Flush batch queue (Requirement 15.3)
   * Sends all batched messages in a single payload
   */
  private flushBatch(): void {
    if (this.batchQueue.length === 0) {
      return;
    }
    
    // Clear timer
    if (this.batchTimer !== null) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    
    // Send batch
    if (this.connectionState === 'connected' && this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        const batchMessage = {
          type: 'batch',
          messages: this.batchQueue,
          timestamp: Date.now(),
        };
        const payload = JSON.stringify(batchMessage);
        this.ws.send(payload);
        
        // Clear batch queue
        this.batchQueue = [];
      } catch (error) {
        console.error('Failed to send batch:', error);
        // Move batch to message queue for retry
        this.messageQueue.push(...this.batchQueue);
        this.batchQueue = [];
      }
    }
  }
  
  /**
   * Register event handler
   */
  on(event: WebSocketEventType, handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.add(handler);
    }
  }
  
  /**
   * Unregister event handler
   */
  off(event: WebSocketEventType, handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }
  
  /**
   * Get current connection state
   */
  getConnectionState(): WebSocketConnectionState {
    return this.connectionState;
  }
  
  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionState === 'connected' && this.ws?.readyState === WebSocket.OPEN;
  }
  
  /**
   * Check if bandwidth is limited (Requirement 15.2)
   * Returns true if connection is slow and should reduce update frequency
   */
  isBandwidthLimited(): boolean {
    return this.bandwidthLimited;
  }
  
  /**
   * Get recommended cursor update interval based on bandwidth (Requirement 15.2)
   * Returns 100ms (10 Hz) for normal, 200ms (5 Hz) for limited bandwidth
   */
  getRecommendedCursorInterval(): number {
    return this.bandwidthLimited ? 200 : 100;
  }
  
  /**
   * Handle WebSocket open event
   */
  private handleOpen(event: Event): void {
    console.log('WebSocket connected');
    this.setConnectionState('connected');
    this.reconnectAttempts = 0;
    
    // Start heartbeat
    this.startHeartbeat();
    
    // Start bandwidth monitoring (Requirement 15.2)
    this.startBandwidthMonitoring();
    
    // Send queued messages
    this.flushMessageQueue();
    
    // Emit events
    this.emit('open', event);
    if (this.reconnectAttempts > 0) {
      this.emit('reconnected', event);
    }
  }
  
  /**
   * Handle WebSocket close event
   */
  private handleClose(event: CloseEvent): void {
    console.log('WebSocket closed:', event.code, event.reason);
    this.clearHeartbeatTimer();
    this.clearBandwidthMonitoring();
    this.setConnectionState('disconnected');
    this.emit('close', event);
    
    // Attempt reconnection if enabled and not a normal closure
    if (this.config.autoReconnect && event.code !== 1000) {
      this.scheduleReconnect();
    }
  }
  
  /**
   * Handle WebSocket error event
   */
  private handleError(event: Event): void {
    console.error('WebSocket error:', event);
    this.setConnectionState('error');
    this.emit('error', event);
  }
  
  /**
   * Handle WebSocket message event
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      
      // Handle heartbeat response
      if (data.type === 'pong') {
        this.lastHeartbeat = Date.now();
        return;
      }
      
      this.emit('message', data);
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }
  
  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }
    
    this.clearReconnectTimer();
    
    // Calculate delay with exponential backoff
    const delay = Math.min(
      this.config.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.config.maxReconnectDelay
    );
    
    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);
    this.setConnectionState('reconnecting');
    this.emit('reconnecting', { attempt: this.reconnectAttempts + 1, delay });
    
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }
  
  /**
   * Clear reconnection timer
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
  
  /**
   * Start heartbeat timer
   */
  private startHeartbeat(): void {
    this.clearHeartbeatTimer();
    this.lastHeartbeat = Date.now();
    
    this.heartbeatTimer = window.setInterval(() => {
      if (this.isConnected()) {
        // Send ping
        this.send({ type: 'ping', timestamp: Date.now() });
        
        // Check if last heartbeat was too long ago
        const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeat;
        if (timeSinceLastHeartbeat > this.config.heartbeatInterval * 2) {
          console.warn('Heartbeat timeout, reconnecting...');
          this.disconnect();
          this.connect();
        }
      }
    }, this.config.heartbeatInterval);
  }
  
  /**
   * Clear heartbeat timer
   */
  private clearHeartbeatTimer(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
  
  /**
   * Flush queued messages
   */
  private flushMessageQueue(): void {
    if (this.messageQueue.length > 0) {
      console.log(`Flushing ${this.messageQueue.length} queued messages`);
      const queue = [...this.messageQueue];
      this.messageQueue = [];
      
      queue.forEach(message => {
        this.send(message);
      });
    }
  }
  
  /**
   * Set connection state and notify handlers
   */
  private setConnectionState(state: WebSocketConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      console.log('WebSocket state changed:', state);
    }
  }
  
  /**
   * Emit event to registered handlers
   */
  private emit(event: WebSocketEventType, data?: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in ${event} handler:`, error);
        }
      });
    }
  }
}
